import { openNotification } from "../../../Components/Notification/Index";
import { useAIAssessmentContext } from "../../../Context/AIAssessmentContext/AIAssessmentContext";
import { useAILoader } from "../../../Context/AILoaderContext/AILoaderContext";
import InterviewService from "../../../Services/interview.service";
import useAiAssessmentStart from "./useAiAssessmentStart";
import aiInterviewService from "../../../Services/ai-interview.service";
import { InterviewSession } from "../../../Interface/Index";
import { useNavigate } from "react-router-dom";
import {
  JobApplication,
  JobApplicationStatus,
} from "../../../Entities/job-application.entity";
import fixWebmDuration from "fix-webm-duration";
import { useCurrentApplication } from "../../../Context/CurrentApplicationContext/CurrentApplicationContext";
import JobApplicationService from "../../../Services/job.application.service";

interface UseAiAssessmentEvaluateReturnType {
  handleEvaluation: (triggerFrom: "system" | "user") => Promise<void>;
  handleEvaluatedSession: (
    updatedSession: InterviewSession,
    questionnaireStatus?: string | null,
  ) => Promise<void>;
  handleQualifiedRound: (updatedSession: InterviewSession) => Promise<void>;
  handleUnqualifiedRound: (updatedSession: InterviewSession) => Promise<void>;
  stopAndUploadRecording: (sessionId?: string) => Promise<void>;
  handleClearAllStates: () => void;
}

const useAiAssessmentEvaluate = (): UseAiAssessmentEvaluateReturnType => {
  const navigate = useNavigate();

  const {
    aiAssessmentQuestions,
    currentSession,
    recordedChunks,
    recordedBlob,
    mediaRecorderRef,
    displayStreamRef,
    cameraMicrophoneStreamRef,
    setShowRetryEvaluation,
    setIsEvaluating,
    setShowCamRecorder,
    setCurrentSession,
    setIsRoundFinished,
    setCurrentStep,
    setExitFullScreenTimer,
    setShowExitFullScreenModal,
    setRecordedBlob,
    clearAIAssessmentState,
    clearProctoringState,
    clearUIState,
    clearModalState,
  } = useAIAssessmentContext();

  const {
    jobPost,
    jobApplication,
    setJobApplication,
    interviewSessions,
    setInterviewSessions,
  } = useCurrentApplication();

  const { toggleToNormalScreenMode } = useAiAssessmentStart();

  const interviewService = new InterviewService();
  const jobApplicationService = new JobApplicationService();

  const { setAiLoaderText, setIsShowAILoader, clearAILoader } = useAILoader();

  const checkSessionEvaluated = async (
    sessionId: string,
  ): Promise<InterviewSession> => {
    return new Promise((resolve, reject) => {
      const interval = setInterval(async () => {
        try {
          const session = await interviewService.findSessionById(sessionId);
          if (session && session.status !== "pending") {
            clearInterval(interval);
            resolve(session);
          }
        } catch (error) {
          clearInterval(interval);
          reject(error);
        }
      }, 2000);
    });
  };

  const checkApplicationStatus = async (
    id: string,
    status: JobApplicationStatus,
  ): Promise<JobApplication> => {
    return new Promise((resolve, reject) => {
      const interval = setInterval(async () => {
        try {
          const jobApplication =
            await jobApplicationService.findJobApplicationById(id);
          if (jobApplication && jobApplication.status === status) {
            clearInterval(interval);
            resolve(jobApplication);
          }
        } catch (error) {
          clearInterval(interval);
          reject(error);
        }
      }, 2000);
    });
  };

  const constCheckNextRoundSession = async (
    applicationId: string,
    roundId: string,
  ): Promise<InterviewSession> => {
    return new Promise((resolve, reject) => {
      const interval = setInterval(async () => {
        try {
          const sessions =
            await interviewService.getAllInterviewSessionById(applicationId);
          if (sessions && sessions?.length > 0) {
            const nextRoundSession = sessions.find(
              (s) => s.interviewRoundId === roundId && s.status === "pending",
            );
            if (nextRoundSession && nextRoundSession?.isQuestionsGenerated) {
              clearInterval(interval);
              resolve(nextRoundSession);
            }
          }
        } catch (error) {
          clearInterval(interval);
          reject(error);
        }
      }, 2000);
    });
  };

  const stopAllMediaStreams = () => {
    console.info("[VideoUpload] stopAllMediaStreams: starting");
    const mr = mediaRecorderRef?.current;
    console.info("[VideoUpload] stopAllMediaStreams: MediaRecorder", {
      exists: !!mr,
      state: mr?.state ?? "none",
    });
    try {
      if (mr && mr.state !== "inactive") {
        mr.stop();
        mediaRecorderRef.current = null;
        console.info(
          "[VideoUpload] stopAllMediaStreams: MediaRecorder stopped and ref cleared",
        );
      }
    } catch (error) {
      console.warn("Error stopping media recorder:", error);
    }

    const camStream = cameraMicrophoneStreamRef?.current;
    console.info("[VideoUpload] stopAllMediaStreams: camera/mic stream", {
      exists: !!camStream,
      trackCount: camStream?.getTracks?.()?.length ?? 0,
    });
    if (camStream) {
      try {
        camStream.getTracks().forEach((track) => {
          console.info(
            "[VideoUpload] stopAllMediaStreams: stopping camera/mic track",
            {
              kind: track.kind,
              enabled: track.enabled,
              readyState: track.readyState,
            },
          );
          track.stop();
        });
        cameraMicrophoneStreamRef.current = null;
        console.info(
          "[VideoUpload] stopAllMediaStreams: camera/mic stream stopped and ref cleared",
        );
      } catch (error) {
        console.warn("Error stopping camera/microphone stream:", error);
      }
    }

    const dispStream = displayStreamRef?.current;
    console.info("[VideoUpload] stopAllMediaStreams: display/screen stream", {
      exists: !!dispStream,
      trackCount: dispStream?.getTracks?.()?.length ?? 0,
    });
    if (dispStream) {
      try {
        dispStream.getTracks().forEach((track) => {
          console.info(
            "[VideoUpload] stopAllMediaStreams: stopping display track",
            {
              kind: track.kind,
              readyState: track.readyState,
            },
          );
          track.stop();
        });
        displayStreamRef.current = null;
        console.info(
          "[VideoUpload] stopAllMediaStreams: display stream stopped and ref cleared",
        );
      } catch (error) {
        console.warn("Error stopping display stream:", error);
      }
    }
    console.info("[VideoUpload] stopAllMediaStreams: completed");
  };

  const handleClearAllStates = () => {
    // Clear all states
    clearAIAssessmentState();
    clearProctoringState();
    clearUIState();
    clearModalState();
    stopAllMediaStreams();

    localStorage.removeItem("sessionToken");
    localStorage.removeItem("refreshedSessionId");
    localStorage.removeItem("refresh");
  };

  const stopAndUploadRecording = async (sessionId?: string): Promise<void> => {
    const targetSessionId = sessionId || currentSession?._id;

    console.info("[VideoUpload] stopAndUploadRecording: called", {
      sessionIdArg: sessionId ?? "none",
      currentSessionId: currentSession?._id ?? "none",
      targetSessionId: targetSessionId ?? "none",
    });

    try {
      if (!targetSessionId) return;

      const fixDuration = (blob: Blob, durationMs: number): Promise<Blob> =>
        new Promise((resolve) => {
          fixWebmDuration(blob, durationMs, resolve);
        });

      const blobPromise: Promise<Blob> = new Promise((resolve) => {
        const finalize = () => {
          try {
            const chunksAtFinalize = recordedChunks?.current ?? [];

            if (chunksAtFinalize.length > 0) {
              resolve(new Blob(chunksAtFinalize, { type: "video/webm" }));
            } else if (recordedBlob) {
              resolve(recordedBlob);
            } else {
              resolve(new Blob());
            }
          } catch {
            resolve(new Blob());
          }
        };

        const mediaRecorder = mediaRecorderRef.current;

        if (!mediaRecorder || mediaRecorder.state === "inactive") {
          return finalize();
        }

        const onStop = () => {
          mediaRecorder.removeEventListener("stop", onStop);
          finalize();
        };

        mediaRecorder.addEventListener("stop", onStop);
      });

      if (
        mediaRecorderRef.current &&
        mediaRecorderRef.current.state !== "inactive"
      ) {
        mediaRecorderRef.current.stop();
      }

      const rawBlob = await blobPromise;

      let finalBlob = rawBlob;

      if (rawBlob && rawBlob.size > 0) {
        const startTime =
          (mediaRecorderRef.current as any)?._startTime ?? Date.now();
        const durationMs = Date.now() - startTime;

        console.info("[VideoUpload] fixing duration", { durationMs });

        finalBlob = await fixDuration(rawBlob, durationMs);
      }

      if (finalBlob && finalBlob.size > 0) {
        await interviewService.storeScreenRecording(targetSessionId, finalBlob);
        console.info("[VideoUpload] upload completed successfully");
      }

      recordedChunks.current = [];
      mediaRecorderRef.current = null;

      if (displayStreamRef?.current) {
        displayStreamRef.current.getTracks().forEach((t) => t.stop());
        displayStreamRef.current = null;
      }

      setRecordedBlob(null);
    } catch (error) {
      console.error("[VideoUpload] stopAndUploadRecording failed:", error);
    }
  };

  const triggerEvaluation = async (triggerFrom: "system" | "user") => {
    const sessionToken = localStorage.getItem("sessionToken");
    if (
      !currentSession ||
      !jobApplication ||
      !aiAssessmentQuestions ||
      aiAssessmentQuestions.length === 0 ||
      !sessionToken ||
      !jobPost
    ) {
      console.error(
        "useAiAssessmentEvaluate: No session or questions or applied job or session token found -> triggerEvaluation",
      );
      return;
    }

    // Show loader and update states
    let message = "Evaluating your answers please wait...";
    if (triggerFrom === "system") {
      message =
        "This assessment's time limit has been reached. Your progress is recorded.";
    }
    setIsShowAILoader(true);
    setAiLoaderText(message);
    setIsEvaluating(true);
    setShowCamRecorder(false);
    toggleToNormalScreenMode();

    try {
      // Uploading recording before evaluation
      await stopAndUploadRecording();

      // Stop mic and camera stream
      stopAllMediaStreams();

      aiInterviewService.evaluateInterview(currentSession._id, sessionToken);

      // poll for evaluated session
      setAiLoaderText("Waiting for results please wait...");
      const updatedSession = await checkSessionEvaluated(currentSession._id);

      // validate evaluated session
      setAiLoaderText("Validating results please wait...");
      const currentRound = jobPost.interviewRounds.find(
        (r) => r.interviewRoundId === currentSession.interviewRoundId,
      );

      const isOptional = !currentRound?.isMustQualify;
      const isQualified = updatedSession.status === "qualified";

      if (isOptional) {
        return await handleQualifiedRound(updatedSession);
      } else {
        if (isQualified) {
          return await handleQualifiedRound(updatedSession);
        } else {
          return await handleUnqualifiedRound(updatedSession);
        }
      }
    } catch (error: any) {
      console.error(
        "useAiAssessmentEvaluate: Failed to trigger evaluation -> triggerEvaluation",
        error,
      );
      openNotification(
        "error",
        "Failed to trigger evaluation",
        "Please try again.",
      );
      clearAILoader();
      setShowRetryEvaluation(true);
      return;
    } finally {
      stopAllMediaStreams();
    }
  };

  const handleEvaluation = async (triggerFrom: "system" | "user") => {
    if (
      !currentSession ||
      !aiAssessmentQuestions ||
      aiAssessmentQuestions.length === 0 ||
      !jobApplication
    ) {
      console.error(
        "useAiAssessmentEvaluate: No session or questions or applied job found -> handleEvaluation",
      );
      return;
    }

    const isSessionLive = currentSession.isLive;
    const isSessionCompleted = currentSession.status !== "pending";
    if (!isSessionLive || isSessionCompleted) {
      console.error(
        "useAiAssessmentEvaluate: Session is not live or completed -> handleEvaluation",
      );
      return;
    }

    const isAtleastOneQuestionAnswered = aiAssessmentQuestions.some(
      (question) => question.answer && question.answer.trim().length > 0,
    );
    if (!isAtleastOneQuestionAnswered && triggerFrom === "user") {
      console.error(
        "useAiAssessmentEvaluate: No questions answered -> handleEvaluation",
      );
      openNotification(
        "error",
        "No questions answered",
        "Please answer at least one question before evaluating",
      );
      return;
    }

    await triggerEvaluation(triggerFrom);

    return;
  };

  const handleEvaluatedSession = async (updatedSession: InterviewSession) => {
    setIsShowAILoader(true);
    setAiLoaderText("Processing evaluation result...");
    const currentRound = jobPost?.interviewRounds.find(
      (r) => r.interviewRoundId === updatedSession.interviewRoundId,
    );

    const isOptional = !currentRound?.isMustQualify;
    const isQualified = updatedSession.status === "qualified";
    if (isOptional) {
      return await handleQualifiedRound(updatedSession);
    } else {
      if (isQualified) {
        return await handleQualifiedRound(updatedSession);
      } else {
        return await handleUnqualifiedRound(updatedSession);
      }
    }
  };

  const handleQualifiedRound = async (updatedSession: InterviewSession) => {
    const sortedRounds = [...(jobPost?.interviewRounds || [])].sort(
      (a, b) => a.interviewRoundNumber - b.interviewRoundNumber,
    );

    const currentIndex = sortedRounds.findIndex(
      (r) => r.interviewRoundId === currentSession?.interviewRoundId,
    );

    const currentRound = sortedRounds[currentIndex];

    const nextRound =
      currentIndex !== -1 && currentIndex < sortedRounds.length - 1
        ? sortedRounds[currentIndex + 1]
        : null;
    const isLastRound =
      sortedRounds[sortedRounds.length - 1].interviewRoundNumber ===
      currentRound?.interviewRoundNumber;

    const isOnlineInterviewEnabled = jobPost?.onlineInterview;

    if (!currentRound) {
      return;
    }

    try {
      setAiLoaderText("Processing evaluation result...");
      if (isLastRound) {
        const updatedApplication =
          await jobApplicationService.findJobApplicationById(
            updatedSession.jobApplicationId,
          );

        // partially qualified
        if (
          updatedApplication?.status === "applied" &&
          updatedApplication.questionnaireStatus === "partially_qualified"
        ) {
          setJobApplication(updatedApplication);
          const updatedSessions = interviewSessions.map((s) =>
            s._id === updatedSession._id ? updatedSession : s,
          );

          setInterviewSessions(updatedSessions);
          setIsShowAILoader(false);
          setAiLoaderText(null);
          localStorage.removeItem("refresh");
          localStorage.removeItem("refreshedSessionId");
          stopAllMediaStreams();
          handleClearAllStates();
          navigate(`/${jobPost?.slug}/requirements?view=result`);
          return;
        }

        if (isOnlineInterviewEnabled) {
          const updatedApplication = await checkApplicationStatus(
            updatedSession.jobApplicationId,
            "interview",
          );
          setJobApplication(updatedApplication);
          const updatedSessions = interviewSessions.map((s) =>
            s._id === updatedSession._id ? updatedSession : s,
          );

          setInterviewSessions(updatedSessions);
          setIsShowAILoader(false);
          setAiLoaderText(null);
          localStorage.removeItem("refresh");
          localStorage.removeItem("refreshedSessionId");
          stopAllMediaStreams();
          handleClearAllStates();
          navigate(`/${jobPost?.slug}/requirements?view=interview`);
          return;
        } else {
          const updatedApplication = await checkApplicationStatus(
            updatedSession.jobApplicationId,
            "shortlisted",
          );
          setJobApplication(updatedApplication);
          const updatedSessions = interviewSessions.map((s) =>
            s._id === updatedSession._id ? updatedSession : s,
          );

          setInterviewSessions(updatedSessions);
          setIsShowAILoader(false);
          setAiLoaderText(null);
          localStorage.removeItem("refresh");
          localStorage.removeItem("refreshedSessionId");
          stopAllMediaStreams();
          handleClearAllStates();
          navigate(`/${jobPost?.slug}/requirements?view=result`);
          return;
        }
      } else {
        setAiLoaderText("Creating next round please wait");
        const nextRoundSession = await constCheckNextRoundSession(
          updatedSession.jobApplicationId,
          nextRound?.interviewRoundId || "",
        );
        const updatedSessions = interviewSessions.map((s) =>
          s._id === updatedSession._id ? updatedSession : s,
        );
        updatedSessions.push(nextRoundSession);

        const updatedApplication =
          await jobApplicationService.findJobApplicationById(
            updatedSession.jobApplicationId,
          );

        if (updatedApplication) {
          setJobApplication(updatedApplication);
        }
        setInterviewSessions(updatedSessions);
        setIsShowAILoader(false);
        setAiLoaderText(null);
        localStorage.removeItem("refresh");
        localStorage.removeItem("refreshedSessionId");
        stopAllMediaStreams();
        handleClearAllStates();
        navigate(
          `/${jobPost?.slug}/interview-round?sessionId=${nextRoundSession._id}`,
        );
        return;
      }
    } catch (error: any) {
      console.error("Error ", error);
    } finally {
      clearAILoader();
    }
  };

  const handleUnqualifiedRound = async (updatedSession: InterviewSession) => {
    setAiLoaderText("Validating updated job application...");
    try {
      const updatedApplication = await checkApplicationStatus(
        updatedSession.jobApplicationId,
        "rejected",
      );

      setShowExitFullScreenModal(false);
      setExitFullScreenTimer(false);
      updatedSession.status = "unqualified";
      setCurrentSession(updatedSession);
      setIsRoundFinished(true);
      await stopAndUploadRecording();
      setCurrentStep(aiAssessmentQuestions.length);
      if (updatedApplication) {
        setJobApplication(updatedApplication);
      }

      const updatedSessions = interviewSessions.map((s) =>
        s._id === updatedSession._id ? updatedSession : s,
      );

      setInterviewSessions(updatedSessions);
      setIsShowAILoader(false);
      setAiLoaderText(null);
      localStorage.removeItem("refresh");
      localStorage.removeItem("refreshedSessionId");
      stopAllMediaStreams();
      handleClearAllStates();
      navigate(`/${jobPost?.slug}/requirements?view=result`);
    } catch (error: any) {
      console.error("Error ", error);
    } finally {
      clearAILoader();
    }
  };

  return {
    handleEvaluation,
    handleEvaluatedSession,
    handleQualifiedRound,
    handleUnqualifiedRound,
    stopAndUploadRecording,
    handleClearAllStates,
  };
};

export default useAiAssessmentEvaluate;
